import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getCookie } from '../lib/cookies';

// Client Onboarding (Nic 2026-09-28): the Onboarding Specialist's page.

const BASE = '/client-onboarding';
const BOARD_KEY = ['client-onboarding'];

export const useClientOnboardingBoard = ({ all = false } = {}) =>
  useQuery(
    [...BOARD_KEY, 'board', all],
    async () => (await axios.get(BASE, { params: all ? { all: 1 } : {} })).data,
    { refetchInterval: 30000, refetchOnWindowFocus: true, keepPreviousData: true },
  );

// Nurture, 50 a page, searched on the server (2026-09-29).
// Stalled (Nic 2026-10-10): reason = a stall category, mine = my clients.
export const useClientOnboardingNurture = ({ page = 1, q = '', reason = '', mine = false } = {}, opts = {}) =>
  useQuery(
    [...BOARD_KEY, 'nurture', page, q, reason, mine],
    async () =>
      (await axios.get(`${BASE}/nurture`, { params: { page, q, reason: reason || undefined, mine: mine ? 1 : undefined } })).data,
    { keepPreviousData: true, refetchOnWindowFocus: true, ...opts },
  );

export const useClientOnboardingClient = (onboardingId) =>
  useQuery(
    [...BOARD_KEY, 'client', onboardingId],
    async () => (await axios.get(`${BASE}/${onboardingId}`)).data,
    { enabled: !!onboardingId, refetchOnWindowFocus: true },
  );

export const useOnboardingByClaim = (claimId, enabled) =>
  useQuery(
    [...BOARD_KEY, 'by-claim', claimId],
    async () => (await axios.get(`${BASE}/by-claim/${claimId}`)).data,
    { enabled: !!claimId && !!enabled },
  );

// Every write refreshes the page and the open client.
const useOnboardingMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => queryClient.invalidateQueries(BOARD_KEY),
  });
};

export const useTakeClient = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/take`)).data,
  );

// Bring a stalled client back by hand, with a reason (Nic 2026-10-12: anyone
// who works the page). A specialist gets them; the manager or an admin can
// pick toUserId, else their old specialist, else the rotation.
export const useAdminReengage = () =>
  useOnboardingMutation(async ({ onboardingId, reason, toUserId }) =>
    (await axios.post(`${BASE}/${onboardingId}/reengage`, { reason, toUserId })).data,
  );

// A verified address for the client (the checklist's address step).
export const useSetOnboardingAddress = () =>
  useOnboardingMutation(async ({ onboardingId, address }) =>
    (await axios.put(`${BASE}/${onboardingId}/address`, address)).data,
  );

// One Case Readiness answer (yes / no / a date; null clears).
export const useSetOnboardingAnswer = () =>
  useOnboardingMutation(async ({ onboardingId, fieldId, value }) =>
    (await axios.put(`${BASE}/${onboardingId}/answers`, { fieldId, value })).data,
  );

// VA.gov not set up on the call: back to Setting up VA.gov (Nic 2026-10-05).
export const useSendBackToVaGov = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/va-gov-send-back`)).data,
  );

// Confirm service agreement (Nic 2026-10-23): Accept the VA check (with an
// optional note), or start one for a client who signed before it existed.
export const useAcceptSa = () =>
  useOnboardingMutation(async ({ onboardingId, note }) =>
    (await axios.post(`${BASE}/${onboardingId}/sa-check/accept`, { note })).data,
  );
export const useStartSaCheck = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/sa-check/start`)).data,
  );

// They set it up but never tapped "I've done it" (Nic 2026-10-10).
export const useConfirmVaGovForThem = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/va-gov-confirm`)).data,
  );

// Tile style (the same setting as the Sales board's) and the desk's Hide
// done (Nic 2026-10-12), saved with the person.
export const useSaveOnboardingDisplayPrefs = () =>
  useOnboardingMutation(async (prefs) => (await axios.patch(`${BASE}/prefs`, prefs)).data);

// Pin a client (Nic 2026-10-12). The star flips at once; the board's
// pinned list follows the server's answer.
export const useSetPinned = () => {
  const queryClient = useQueryClient();
  const boardKey = [...BOARD_KEY, 'board', false];
  return useMutation(
    async ({ onboardingId, pinned }) => (await axios.put(`${BASE}/${onboardingId}/pin`, { pinned })).data,
    {
      onMutate: async ({ onboardingId, pinned }) => {
        await queryClient.cancelQueries(boardKey);
        const before = queryClient.getQueryData(boardKey);
        if (before) {
          const rest = (before.pinned || []).filter((id) => id !== onboardingId);
          queryClient.setQueryData(boardKey, { ...before, pinned: pinned ? [...rest, onboardingId] : rest });
        }
        return { before };
      },
      onError: (_e, _v, ctx) => {
        if (ctx?.before) queryClient.setQueryData(boardKey, ctx.before);
      },
      onSuccess: (data) => {
        const now = queryClient.getQueryData(boardKey);
        if (now && Array.isArray(data?.pinned)) queryClient.setQueryData(boardKey, { ...now, pinned: data.pinned });
      },
    },
  );
};

// Mark their first meeting happened, with a short reason (Nic 2026-10-12:
// for anything not booked in Calendly), and its 5 minute undo.
export const useStartFirstCall = () =>
  useOnboardingMutation(async ({ onboardingId, reason }) =>
    (await axios.post(`${BASE}/${onboardingId}/first-call`, { reason })).data,
  );
export const useUndoFirstCall = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/first-call-undo`)).data,
  );

export const useLogTouch = () =>
  useOnboardingMutation(async ({ onboardingId, kind, note }) =>
    (await axios.post(`${BASE}/${onboardingId}/touch`, { kind, note })).data,
  );

// Carried over at launch, back to the checklist (Nic 2026-10-11).
export const useWorkChecklist = () =>
  useOnboardingMutation(async ({ onboardingId }) =>
    (await axios.post(`${BASE}/${onboardingId}/work-checklist`)).data,
  );

// The desk's Today list (Nic 2026-10-11): each person's own to-dos, plus
// the suggestions they checked off (autoKey).
const TODOS_KEY = [...BOARD_KEY, 'todos'];
export const useOnboardingTodos = (enabled = true) =>
  useQuery(TODOS_KEY, async () => (await axios.get(`${BASE}/todos`)).data, {
    enabled: !!enabled,
    refetchOnWindowFocus: true,
  });
const useTodoMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, { onSuccess: () => queryClient.invalidateQueries(TODOS_KEY) });
};
export const useAddTodo = () =>
  useTodoMutation(async (body) => (await axios.post(`${BASE}/todos`, body)).data);
export const useUpdateTodo = () =>
  useTodoMutation(async ({ todoId, ...body }) => (await axios.patch(`${BASE}/todos/${todoId}`, body)).data);
export const useDeleteTodo = () =>
  useTodoMutation(async ({ todoId }) => (await axios.delete(`${BASE}/todos/${todoId}`)).data);

export const useMarkDead = () =>
  useOnboardingMutation(async ({ onboardingId, reason, reasonDetail }) =>
    (await axios.post(`${BASE}/${onboardingId}/dead`, { reason, reasonDetail })).data,
  );

// Expedited (Nic 2026-10-26): their specialist marks or unmarks it.
export const useExpedite = () =>
  useOnboardingMutation(async ({ onboardingId, on, reason, note }) =>
    on
      ? (await axios.post(`${BASE}/${onboardingId}/expedite`, { reason, note })).data
      : (await axios.delete(`${BASE}/${onboardingId}/expedite`)).data,
  );


export const useSpecialists = (enabled) =>
  useQuery(
    [...BOARD_KEY, 'specialists'],
    async () => (await axios.get(`${BASE}/specialists`)).data,
    { enabled: !!enabled, staleTime: 5 * 60 * 1000 },
  );

export const useReassignClient = () =>
  useOnboardingMutation(async ({ onboardingId, toUserId }) =>
    (await axios.post(`${BASE}/${onboardingId}/reassign`, { toUserId })).data,
  );

export const fetchOnboardingTextDraft = async (onboardingId) =>
  (await axios.get(`${BASE}/${onboardingId}/text-draft`)).data;

export const useSendOnboardingText = () =>
  useOnboardingMutation(async ({ onboardingId, text, rcAccessToken }) =>
    (await axios.post(`${BASE}/${onboardingId}/text`, { text, rcAccessToken })).data,
  );

// The lead's notes, the same ones Sales wrote (Nic 2026-10-09).
export const useSaveOnboardingNotes = () =>
  useOnboardingMutation(async ({ onboardingId, notes }) =>
    (await axios.put(`${BASE}/${onboardingId}/notes`, { notes })).data,
  );

// Fix their birthday or SSN (Nic 2026-10-25, About them). Reveals are a
// plain GET in IdentityRows, never cached.
export const useSaveOnboardingIdentity = () =>
  useOnboardingMutation(async ({ onboardingId, ssn, dob }) =>
    (await axios.put(`${BASE}/${onboardingId}/identity`, { ssn, dob })).data,
  );

// Their Service Agreement (Nic 2026-10-09): the signup row the shared Facts
// and Agreement sections read, and the same api object Sales and Signups
// gave them, pointed at Client Onboarding's owner-only routes.
export const useClientAgreement = (onboardingId, enabled = true) =>
  useQuery(
    [...BOARD_KEY, 'agreement', onboardingId],
    async () => (await axios.get(`${BASE}/${onboardingId}/agreement`)).data,
    { enabled: !!onboardingId && enabled, refetchOnWindowFocus: true },
  );

export const onboardingAgreementApiFor = (onboardingId) => {
  const post = async (path, body) =>
    (await axios.post(`${BASE}/${onboardingId}/agreement/${path}`, body)).data;
  return {
    confirmFacts: (facts) => post('confirm-facts', facts),
    sendAgreement: ({ regenerate = false } = {}) => post('send-service-agreement', { regenerate }),
    voidAgreement: () => post('void-agreement'),
    signLink: () => post('sign-link'),
    beginCorrection: (why) => post('begin-correction', why),
    cancelCorrection: () => post('cancel-correction'),
  };
};

// Signed SAs (Nic 2026-10-10): every signed agreement, paged on the server;
// stage = active | onboarding | with_cm | billing | paid | dead | correcting.
export const useSignedSas = ({ page = 1, q = '', stage = '' } = {}) =>
  useQuery(
    [...BOARD_KEY, 'signed-sas', page, q, stage],
    async () =>
      (await axios.get(`${BASE}/signed-sas`, { params: { page, q, stage: stage || undefined } })).data,
    { keepPreviousData: true, refetchOnWindowFocus: true },
  );

export const useSignedSa = (userId) =>
  useQuery(
    [...BOARD_KEY, 'signed-sa', userId],
    async () => (await axios.get(`${BASE}/signed-sas/${userId}`)).data,
    { enabled: !!userId, refetchOnWindowFocus: true },
  );

export const signedSaApiFor = (userId) => {
  const post = async (path, body) =>
    (await axios.post(`${BASE}/signed-sas/${userId}/${path}`, body)).data;
  return {
    confirmFacts: (facts) => post('confirm-facts', facts),
    sendAgreement: ({ regenerate = false } = {}) => post('send-service-agreement', { regenerate }),
    voidAgreement: () => post('void-agreement'),
    signLink: () => post('sign-link'),
    beginCorrection: (why) => post('begin-correction', why),
    cancelCorrection: () => post('cancel-correction'),
  };
};

// Mirror of server/lib/clientOnboarding.js DEAD_REASONS.
export const ONBOARDING_DEAD_REASONS = [
  'Changed their mind',
  'Stopped responding',
  'Not eligible',
  'Went with someone else',
  'Other',
];

// ---------------------------------------------------------------------------
// Client activity (Nic 2026-10-23): what clients did, what's new to you, and
// a live stream so the page updates the moment they do something. "Seen" is
// kept on the server per person, so a cleared item never comes back.
// ---------------------------------------------------------------------------

export const useClientActivity = (scope = 'mine', opts = {}) =>
  useQuery(
    [...BOARD_KEY, 'activity', scope],
    async () => (await axios.get(`${BASE}/activity`, { params: { scope } })).data,
    { refetchInterval: 60000, refetchOnWindowFocus: true, keepPreviousData: true, ...opts },
  );

// Seen: one client (opened) or, with no onboardingId, everything.
export const useMarkActivitySeen = () => {
  const queryClient = useQueryClient();
  return useMutation(async ({ onboardingId } = {}) => (await axios.post(`${BASE}/activity/seen`, onboardingId ? { onboardingId } : {})).data, {
    // Cleared on screen at once (no count lingering while the save lands);
    // the refetch after confirms it from the server.
    onMutate: async ({ onboardingId } = {}) => {
      await queryClient.cancelQueries([...BOARD_KEY, 'activity']);
      const hit = (id) => !onboardingId || id === onboardingId;
      queryClient.setQueriesData([...BOARD_KEY, 'activity'], (old) => {
        if (!old?.items) return old;
        const items = old.items.map((i) => (i.isNew && hit(i.onboardingId) ? { ...i, isNew: false } : i));
        return { ...old, items, newCount: items.filter((i) => i.isNew).length };
      });
      queryClient.setQueriesData([...BOARD_KEY, 'board'], (old) => {
        if (!old?.columns) return old;
        const columns = Object.fromEntries(
          Object.entries(old.columns).map(([k, list]) => [
            k,
            (list || []).map((c) => (c.newActivity && hit(c.onboardingId) ? { ...c, newActivity: null } : c)),
          ]),
        );
        return { ...old, columns };
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries([...BOARD_KEY, 'activity']);
      queryClient.invalidateQueries([...BOARD_KEY, 'board']);
    },
  });
};

const STREAM_DEBOUNCE_MS = 300;

/**
 * The Workspace's live updates (GET /client-onboarding/stream): any change to
 * a client refreshes the page's data; `onEvent` hears each one. Reconnects
 * with backoff. Same transport as the Sales board's stream.
 */
export const useClientOnboardingStream = (enabled = true, onEvent = null) => {
  const queryClient = useQueryClient();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let controller = null;
    let retryDelay = 1000;
    let debounce = null;
    const refresh = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        queryClient.invalidateQueries(BOARD_KEY);
        // A checklist change can be an FOTC: the scoreboard's chip and card.
        queryClient.invalidateQueries(['onboarding-scoreboard']);
      }, STREAM_DEBOUNCE_MS);
    };
    const connect = async () => {
      while (!cancelled) {
        controller = new AbortController();
        try {
          const token = getCookie('token');
          const response = await fetch(`${axios.defaults.baseURL || ''}${BASE}/stream`, {
            headers: { Accept: 'text/event-stream', ...(token && { Authorization: `Bearer ${token}` }) },
            signal: controller.signal,
          });
          if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);
          retryDelay = 1000;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done || cancelled) break;
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split('\n\n');
            buffer = frames.pop();
            frames.forEach((frame) => {
              const line = frame.split('\n').find((l) => l.startsWith('data: '));
              if (!line) return;
              refresh();
              try {
                onEventRef.current?.(JSON.parse(line.slice(6)));
              } catch {
                /* a bad frame never kills the stream */
              }
            });
          }
        } catch {
          if (cancelled) break;
        }
        if (cancelled) break;
        const delay = retryDelay;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, delay));
        retryDelay = Math.min(retryDelay * 2, 15000);
      }
    };
    connect();
    return () => {
      cancelled = true;
      clearTimeout(debounce);
      controller?.abort();
    };
  }, [enabled, queryClient]);
};
